import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';

import type { IFilters } from '../queries/banners.graphql';
import { selectCountry, selectJurisdiction } from '../store/locale.store';
import { applyDemographicsFilter, Device } from '../utils/demographic';

import { useDesktop } from './breakpoint';
import { useUserTags } from './use-user-tags';

interface IObjectWithFilters {
    filters?: IFilters;
}

type FilterArray<T> = Array<Exclude<T, null | undefined>>;

export function useDemographicsFilter<ObjectType extends IObjectWithFilters>(
    arrayList: ObjectType[],
): FilterArray<typeof arrayList[number]> {
    const params = useDemographicsFilterParams();
    return useMemo(() => {
        return applyDemographicsFilter(arrayList, params);
    }, [arrayList, params]);
}

export function useDemographicsFilterParams() {
    const country = useSelector(selectCountry);
    const isAuthenticated = useAuth().isAuthenticated;
    const jurisdiction = useSelector(selectJurisdiction);
    const currentTime = useMemo(() => new Date().getTime(), []);
    const isDesktop = useDesktop();
    const tags = useUserTags();

    return useMemo(() => {
        return {
            isAuthenticated,
            countryCode: country,
            jurisdiction,
            currentTime,
            tags,
            device: isDesktop ? Device.DESKTOP : Device.MOBILE,
        };
    }, [isAuthenticated, country, jurisdiction, currentTime, isDesktop, tags]);
}
